// M8-L: Componente de Formulario de Canalización ATS NOM-035
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertTriangle, Clock, User, Calendar, Building2, FileText, Upload,
  Mail, Phone, MessageCircle, Check, X, Shield, AlertCircle, FileCheck,
  ChevronRight, Info, Activity
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { useLanguage } from '../contexts/LanguageContext';

// Componente para el formulario de canalización estructurado M8-L
const FormularioCanalizacionM8L = ({ caso, empresaId, onSuccess, onCancel }) => {
  const { language } = useLanguage();
  // Campos obligatorios M8-L
  const [canalTipo, setCanalTipo] = useState('');
  const [canalInstitucion, setCanalInstitucion] = useState('');
  const [canalFecha, setCanalFecha] = useState(new Date().toISOString().split('T')[0]);
  const [constanciaFile, setConstanciaFile] = useState(null);
  const [constanciaUrl, setConstanciaUrl] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Campos opcionales
  const [canalNotas, setCanalNotas] = useState('');
  
  // Notificación al empleado
  const [notificacionTipo, setNotificacionTipo] = useState('');
  
  const [saving, setSaving] = useState(false);

  const texts = {
    es: {
      title: 'Registrar Canalización',
      subtitle: 'Complete todos los campos obligatorios para registrar la canalización',
      warning: 'Este registro es INMUTABLE. Una vez guardado no podrá modificarse.',
      
      tipoAtencion: 'Tipo de atención *',
      medica: 'Médica',
      psicologica: 'Psicológica',
      ambas: 'Ambas',
      
      institucion: 'Institución o servicio de atención *',
      institucionPlaceholder: 'Nombre del hospital, clínica o profesional...',
      institucionHelp: 'Mínimo 5 caracteres, máximo 200',
      
      fecha: 'Fecha de canalización *',
      fechaHelp: 'No puede ser futura ni anterior a la identificación del ATS',
      
      constancia: 'Constancia o evidencia documental *',
      constanciaHelp: 'PDF, JPG, PNG o DOCX. Máximo 10MB. OBLIGATORIO para validez legal.',
      selectFile: 'Seleccionar archivo',
      fileSelected: 'Archivo seleccionado',
      uploading: 'Subiendo...',
      uploadSuccess: 'Archivo subido correctamente',
      
      notificacion: '¿Cómo se notificó al empleado? *',
      emailMevia: 'Email automático desde MEVIA',
      enPersona: 'En persona',
      llamada: 'Llamada telefónica',
      whatsapp: 'WhatsApp',
      emailExterno: 'Email externo',
      otro: 'Otro medio',
      
      notas: 'Notas internas (opcional)',
      notasPlaceholder: 'Notas confidenciales para el responsable NOM-035...',
      notasHelp: 'Máximo 500 caracteres',
      
      btnRegistrar: 'Registrar Canalización',
      btnCancel: 'Cancelar',
      btnDisabled: 'Complete todos los campos y suba la constancia'
    },
    en: {
      title: 'Register Referral',
      subtitle: 'Complete all required fields to register the referral',
      warning: 'This record is IMMUTABLE. Once saved it cannot be modified.',
      
      tipoAtencion: 'Type of care *',
      medica: 'Medical',
      psicologica: 'Psychological',
      ambas: 'Both',
      
      institucion: 'Institution or care service *',
      institucionPlaceholder: 'Name of hospital, clinic or professional...',
      institucionHelp: 'Minimum 5 characters, maximum 200',
      
      fecha: 'Referral date *',
      fechaHelp: 'Cannot be future or before ATS identification date',
      
      constancia: 'Certificate or documentary evidence *',
      constanciaHelp: 'PDF, JPG, PNG or DOCX. Maximum 10MB. REQUIRED for legal validity.',
      selectFile: 'Select file',
      fileSelected: 'File selected',
      uploading: 'Uploading...',
      uploadSuccess: 'File uploaded successfully',
      
      notificacion: 'How was the employee notified? *',
      emailMevia: 'Automatic email from MEVIA',
      enPersona: 'In person',
      llamada: 'Phone call',
      whatsapp: 'WhatsApp',
      emailExterno: 'External email',
      otro: 'Other means',
      
      notas: 'Internal notes (optional)',
      notasPlaceholder: 'Confidential notes for NOM-035 responsible...',
      notasHelp: 'Maximum 500 characters',
      
      btnRegistrar: 'Register Referral',
      btnCancel: 'Cancel',
      btnDisabled: 'Complete all fields and upload certificate'
    }
  };
  const t = texts[language] || texts.es;

  // M8-L: Validar formulario
  const isFormValid = () => {
    return canalTipo && 
           canalInstitucion.trim().length >= 5 && 
           canalInstitucion.trim().length <= 200 &&
           canalFecha && 
           constanciaUrl && 
           notificacionTipo;
  };

  // M8-L: Subir archivo de constancia
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validar extensión
    const validExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.docx', '.doc'];
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!validExtensions.includes(ext)) {
      toast.error(language === 'es' 
        ? 'Formato no válido. Use PDF, JPG, PNG o DOCX'
        : 'Invalid format. Use PDF, JPG, PNG or DOCX');
      return;
    }
    
    // Validar tamaño (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(language === 'es' 
        ? 'El archivo excede 10MB'
        : 'File exceeds 10MB');
      return;
    }
    
    setConstanciaFile(file);
    setUploadingFile(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(
        `/v5/nom035/${empresaId}/guide1/upload-constancia`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (res.data.success) {
        setConstanciaUrl(res.data.data.url);
        toast.success(t.uploadSuccess);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error uploading file');
      setConstanciaFile(null);
    } finally {
      setUploadingFile(false);
    }
  };

  // M8-L: Registrar canalización
  const handleSubmit = async () => {
    if (!isFormValid()) return;
    
    setSaving(true);
    try {
      const res = await axios.post(`/v5/nom035/${empresaId}/guide1/canalizar/${caso.user_id}`, {
        canal_tipo: canalTipo,
        canal_institucion: canalInstitucion.trim(),
        canal_fecha: canalFecha,
        canal_constancia_url: constanciaUrl,
        canal_notas: canalNotas.trim() || null,
        canal_notificacion_tipo: notificacionTipo
      });
      
      if (res.data.success) {
        toast.success(language === 'es' 
          ? 'Canalización registrada exitosamente'
          : 'Referral registered successfully');
        onSuccess?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error registering referral');
    } finally {
      setSaving(false);
    }
  };

  // Calcular fecha mínima (fecha de identificación)
  const fechaMinima = caso.fecha_identificacion 
    ? new Date(caso.fecha_identificacion).toISOString().split('T')[0] 
    : undefined;
  const fechaMaxima = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-5">
      {/* Warning inmutable */}
      <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg flex items-start gap-2">
        <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-yellow-800 font-medium">{t.warning}</p>
      </div>

      {/* Encabezado del empleado (solo lectura) */}
      <div className="p-4 bg-gray-50 rounded-lg border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <User className="w-5 h-5 text-gray-500" />
            <div>
              <p className="font-semibold">{caso.nombre}</p>
              <p className="text-sm text-gray-500">{caso.email}</p>
            </div>
          </div>
          {caso.vencido ? (
            <Badge className="bg-red-100 text-red-800 border-red-300">
              <AlertCircle className="w-3 h-3 mr-1" />
              VENCIDO
            </Badge>
          ) : caso.urgente ? (
            <Badge className="bg-orange-100 text-orange-800 border-orange-300">
              <Clock className="w-3 h-3 mr-1" />
              URGENTE
            </Badge>
          ) : (
            <Badge className="bg-blue-100 text-blue-800">
              <Clock className="w-3 h-3 mr-1" />
              {caso.dias_habiles_restantes || (5 - caso.dias_transcurridos)} días restantes
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <span><Building2 className="w-4 h-4 inline mr-1" />{caso.departamento || 'Sin departamento'}</span>
          <span><Calendar className="w-4 h-4 inline mr-1" />Identificado: {new Date(caso.fecha_identificacion).toLocaleDateString()}</span>
        </div>
      </div>

      {/* Formulario */}
      <div className="space-y-4">
        {/* Tipo de atención */}
        <div className="space-y-2">
          <Label className="font-medium">{t.tipoAtencion}</Label>
          <RadioGroup value={canalTipo} onValueChange={setCanalTipo} className="flex gap-4">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="medica" id="medica" />
              <Label htmlFor="medica" className="cursor-pointer">{t.medica}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="psicologica" id="psicologica" />
              <Label htmlFor="psicologica" className="cursor-pointer">{t.psicologica}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="ambas" id="ambas" />
              <Label htmlFor="ambas" className="cursor-pointer">{t.ambas}</Label>
            </div>
          </RadioGroup>
        </div>

        {/* Institución */}
        <div className="space-y-2">
          <Label className="font-medium">{t.institucion}</Label>
          <Input
            value={canalInstitucion}
            onChange={(e) => setCanalInstitucion(e.target.value)}
            placeholder={t.institucionPlaceholder}
            maxLength={200}
          />
          <p className="text-xs text-gray-500">{t.institucionHelp} ({canalInstitucion.length}/200)</p>
        </div>

        {/* Fecha */}
        <div className="space-y-2">
          <Label className="font-medium">{t.fecha}</Label>
          <Input
            type="date"
            value={canalFecha}
            onChange={(e) => setCanalFecha(e.target.value)}
            min={fechaMinima}
            max={fechaMaxima}
          />
          <p className="text-xs text-gray-500">{t.fechaHelp}</p>
        </div>

        {/* Constancia (obligatorio) */}
        <div className="space-y-2">
          <Label className="font-medium flex items-center gap-2">
            <FileText className="w-4 h-4" />
            {t.constancia}
          </Label>
          <div className={`border-2 border-dashed rounded-lg p-4 ${constanciaUrl ? 'border-green-300 bg-green-50' : 'border-gray-300'}`}>
            {constanciaUrl ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-green-700">
                  <FileCheck className="w-5 h-5" />
                  <span className="font-medium">{t.fileSelected}: {constanciaFile?.name || 'documento'}</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => { setConstanciaUrl(''); setConstanciaFile(null); }}
                  className="text-red-600"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="text-center">
                <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                <label className="cursor-pointer">
                  <span className="text-teal-600 hover:text-teal-700 font-medium">
                    {uploadingFile ? t.uploading : t.selectFile}
                  </span>
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.docx,.doc"
                    onChange={handleFileUpload}
                    disabled={uploadingFile}
                  />
                </label>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <Info className="w-3 h-3" /> {t.constanciaHelp}
          </p>
        </div>

        {/* Notificación al empleado */}
        <div className="space-y-2">
          <Label className="font-medium">{t.notificacion}</Label>
          <Select value={notificacionTipo} onValueChange={setNotificacionTipo}>
            <SelectTrigger>
              <SelectValue placeholder={language === 'es' ? 'Seleccionar...' : 'Select...'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="email_mevia">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" /> {t.emailMevia}
                </div>
              </SelectItem>
              <SelectItem value="en_persona">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" /> {t.enPersona}
                </div>
              </SelectItem>
              <SelectItem value="llamada">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" /> {t.llamada}
                </div>
              </SelectItem>
              <SelectItem value="whatsapp">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" /> {t.whatsapp}
                </div>
              </SelectItem>
              <SelectItem value="email_externo">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" /> {t.emailExterno}
                </div>
              </SelectItem>
              <SelectItem value="otro">
                <div className="flex items-center gap-2">
                  <ChevronRight className="w-4 h-4" /> {t.otro}
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Notas internas (opcional) */}
        <div className="space-y-2">
          <Label className="font-medium">{t.notas}</Label>
          <Textarea
            value={canalNotas}
            onChange={(e) => setCanalNotas(e.target.value)}
            placeholder={t.notasPlaceholder}
            maxLength={500}
            rows={3}
          />
          <p className="text-xs text-gray-500">{t.notasHelp} ({canalNotas.length}/500)</p>
        </div>
      </div>

      {/* Botones */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          {t.btnCancel}
        </Button>
        <Button 
          onClick={handleSubmit} 
          disabled={!isFormValid() || saving}
          className={isFormValid() ? 'bg-teal-600 hover:bg-teal-700' : ''}
          title={!isFormValid() ? t.btnDisabled : ''}
        >
          {saving ? (
            <>Guardando...</>
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              {t.btnRegistrar}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};


// Componente principal del modal de Canalización ATS M8-L
const CanalizacionATSM8L = ({ open, onOpenChange, empresaId, onComplete }) => {
  const { language } = useLanguage();
  const [casos, setCasos] = useState([]);
  const [stats, setStats] = useState({ total: 0, pendientes: 0, canalizados: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedCaso, setSelectedCaso] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [trackingCase, setTrackingCase] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [trackingNota, setTrackingNota] = useState('');
  const [trackingSaving, setTrackingSaving] = useState(false);

  const texts = {
    es: {
      title: 'Gestión de Canalizaciones ATS',
      subtitle: 'M8-L: Canalización estructurada con evidencia documental obligatoria',
      pendientes: 'Pendientes',
      canalizados: 'Canalizados',
      total: 'Total casos',
      noCasos: 'No hay casos de ATS identificados',
      urgente: 'URGENTE',
      vencido: 'VENCIDO',
      diasRestantes: 'días restantes',
      canalizar: 'Canalizar',
      verDetalle: 'Ver detalle',
      canalizado: 'Canalizado',
      cerrar: 'Cerrar'
    },
    en: {
      title: 'ATS Referral Management',
      subtitle: 'M8-L: Structured referral with mandatory documentary evidence',
      pendientes: 'Pending',
      canalizados: 'Referred',
      total: 'Total cases',
      noCasos: 'No ATS cases identified',
      urgente: 'URGENT',
      vencido: 'OVERDUE',
      diasRestantes: 'days remaining',
      canalizar: 'Refer',
      verDetalle: 'View details',
      canalizado: 'Referred',
      cerrar: 'Close'
    }
  };
  const t = texts[language] || texts.es;

  const SEGUIMIENTO_STATES = {
    PENDIENTE: { label_es: 'Pendiente', label_en: 'Pending', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
    CITA_AGENDADA: { label_es: 'Cita agendada', label_en: 'Appointment scheduled', color: 'bg-blue-100 text-blue-800 border-blue-300' },
    EXAMEN_REALIZADO: { label_es: 'Examen realizado', label_en: 'Exam completed', color: 'bg-green-100 text-green-800 border-green-300' },
    NO_PROCEDENTE: { label_es: 'No procedente', label_en: 'Not applicable', color: 'bg-gray-100 text-gray-800 border-gray-300' }
  };

  const loadTracking = async (resultId) => {
    try {
      const res = await axios.get(`/v5/nom035/${empresaId}/canalizacion/${resultId}/seguimiento`);
      if (res.data.success) {
        setTrackingData(res.data.data);
        setTrackingCase(resultId);
      }
    } catch (e) { toast.error('Error loading tracking'); }
  };

  const updateTracking = async (field, value) => {
    setTrackingSaving(true);
    try {
      await axios.put(`/v5/nom035/${empresaId}/canalizacion/${trackingCase}/seguimiento`, { [field]: value });
      await loadTracking(trackingCase);
      toast.success(language === 'es' ? 'Seguimiento actualizado' : 'Tracking updated');
    } catch (e) { toast.error('Error'); }
    finally { setTrackingSaving(false); }
  };

  const addTrackingNote = async () => {
    if (!trackingNota.trim()) return;
    setTrackingSaving(true);
    try {
      await axios.post(`/v5/nom035/${empresaId}/canalizacion/${trackingCase}/notas`, {
        nota: trackingNota, autor_nombre: 'Admin'
      });
      setTrackingNota('');
      await loadTracking(trackingCase);
    } catch (e) { toast.error('Error'); }
    finally { setTrackingSaving(false); }
  };

  useEffect(() => {
    if (open) {
      loadCasos();
    }
  }, [open]);

  const loadCasos = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/v5/nom035/${empresaId}/guide1/pending-referrals`);
      if (res.data.success) {
        setCasos(res.data.data.casos || []);
        setStats({
          total: res.data.data.total || 0,
          pendientes: res.data.data.pendientes || 0,
          canalizados: res.data.data.canalizados || 0
        });
      }
    } catch (error) {
      toast.error('Error loading ATS cases');
    } finally {
      setLoading(false);
    }
  };

  const handleCanalizarClick = (caso) => {
    setSelectedCaso(caso);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setSelectedCaso(null);
    loadCasos();
    onComplete?.();
  };

  // Vista del formulario
  if (showForm && selectedCaso) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-teal-600" />
              {t.title}
            </DialogTitle>
          </DialogHeader>
          <FormularioCanalizacionM8L
            caso={selectedCaso}
            empresaId={empresaId}
            onSuccess={handleFormSuccess}
            onCancel={() => { setShowForm(false); setSelectedCaso(null); }}
          />
        </DialogContent>
      </Dialog>
    );
  }

  // Vista principal - lista de casos
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-teal-600" />
            {t.title}
          </DialogTitle>
          <DialogDescription>{t.subtitle}</DialogDescription>
        </DialogHeader>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
            <span className="text-2xl font-bold text-red-700">{stats.pendientes}</span>
            <p className="text-xs text-gray-600">{t.pendientes}</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
            <span className="text-2xl font-bold text-green-700">{stats.canalizados}</span>
            <p className="text-xs text-gray-600">{t.canalizados}</p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg border">
            <span className="text-2xl font-bold text-gray-700">{stats.total}</span>
            <p className="text-xs text-gray-600">{t.total}</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          </div>
        ) : casos.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Shield className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>{t.noCasos}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {casos.map(caso => (
              <div 
                key={caso.result_id}
                className={`p-4 rounded-lg border ${
                  caso.canalizado 
                    ? 'bg-green-50 border-green-200' 
                    : caso.vencido 
                      ? 'bg-red-50 border-red-300'
                      : caso.urgente 
                        ? 'bg-orange-50 border-orange-300'
                        : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      caso.canalizado ? 'bg-green-200' : caso.vencido ? 'bg-red-200' : 'bg-gray-200'
                    }`}>
                      {caso.canalizado ? (
                        <Check className="w-5 h-5 text-green-700" />
                      ) : (
                        <User className="w-5 h-5 text-gray-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{caso.nombre}</p>
                      <p className="text-sm text-gray-500">{caso.departamento || caso.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {caso.canalizado ? (
                      <Badge className="bg-green-100 text-green-800 border-green-300">
                        <Check className="w-3 h-3 mr-1" />
                        {t.canalizado}
                      </Badge>
                    ) : caso.vencido ? (
                      <Badge className="bg-red-100 text-red-800 border-red-300 animate-pulse">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {t.vencido}
                      </Badge>
                    ) : caso.urgente ? (
                      <Badge className="bg-orange-100 text-orange-800 border-orange-300">
                        <Clock className="w-3 h-3 mr-1" />
                        {t.urgente}
                      </Badge>
                    ) : (
                      <Badge className="bg-blue-100 text-blue-800">
                        <Clock className="w-3 h-3 mr-1" />
                        {caso.dias_habiles_restantes || (5 - caso.dias_transcurridos)} {t.diasRestantes}
                      </Badge>
                    )}
                    
                    {!caso.canalizado && (
                      <Button 
                        size="sm" 
                        onClick={() => handleCanalizarClick(caso)}
                        className="bg-teal-600 hover:bg-teal-700"
                      >
                        {t.canalizar}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* M8-L: Mostrar detalles de canalización si ya está canalizado */}
                {caso.canalizado && caso.canal_tipo && (
                  <div className="mt-3 pt-3 border-t border-green-200 text-sm text-gray-600">
                    <div className="grid grid-cols-2 gap-2">
                      <div><strong>Tipo:</strong> {caso.canal_tipo}</div>
                      <div><strong>Institución:</strong> {caso.canal_institucion}</div>
                      <div><strong>Fecha:</strong> {caso.canal_fecha ? new Date(caso.canal_fecha).toLocaleDateString() : '-'}</div>
                      <div><strong>Notificado vía:</strong> {caso.canal_notificacion_tipo?.replace('_', ' ')}</div>
                    </div>
                    {caso.canal_constancia_url && (
                      <div className="mt-2">
                        <a 
                          href={caso.canal_constancia_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-teal-600 hover:underline flex items-center gap-1"
                        >
                          <FileCheck className="w-4 h-4" /> Ver constancia
                        </a>
                      </div>
                    )}
                    {/* Seguimiento post-canalización */}
                    <div className="mt-2">
                      <Button 
                        variant="outline" size="sm" className="w-full text-xs"
                        onClick={() => loadTracking(caso.result_id)}
                        data-testid={`tracking-btn-${caso.result_id}`}
                      >
                        <Activity className="w-3 h-3 mr-1" />
                        {language === 'es' ? 'Seguimiento post-canalización' : 'Post-referral tracking'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Tracking panel for this case */}
                {trackingCase === caso.result_id && trackingData && (
                  <div className="mt-3 pt-3 border-t border-teal-200 bg-teal-50/50 rounded-lg p-3 space-y-3" data-testid="tracking-panel">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-sm flex items-center gap-1">
                        <Activity className="w-4 h-4 text-teal-600" />
                        {language === 'es' ? 'Seguimiento' : 'Tracking'}
                      </h4>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setTrackingCase(null); setTrackingData(null); }}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    {trackingData.alerta_15_dias && (
                      <div className="flex items-center gap-2 p-2 bg-red-100 rounded text-red-700 text-xs">
                        <AlertCircle className="w-4 h-4" />
                        {language === 'es' 
                          ? `Alerta: ${trackingData.dias_pendiente} días sin seguimiento`
                          : `Alert: ${trackingData.dias_pendiente} days without follow-up`}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{language === 'es' ? 'Estado:' : 'Status:'}</span>
                      <select 
                        className="text-xs rounded border px-2 py-1 bg-white"
                        value={trackingData.seguimiento_estado}
                        onChange={(e) => updateTracking('seguimiento_estado', e.target.value)}
                        disabled={trackingSaving}
                        data-testid="tracking-status-select"
                      >
                        {Object.entries(SEGUIMIENTO_STATES).map(([key, val]) => (
                          <option key={key} value={key}>{language === 'es' ? val.label_es : val.label_en}</option>
                        ))}
                      </select>
                      <Badge className={SEGUIMIENTO_STATES[trackingData.seguimiento_estado]?.color || ''}>
                        {language === 'es'
                          ? SEGUIMIENTO_STATES[trackingData.seguimiento_estado]?.label_es
                          : SEGUIMIENTO_STATES[trackingData.seguimiento_estado]?.label_en}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <label className="text-gray-500">{language === 'es' ? 'Fecha examen' : 'Exam date'}</label>
                        <input type="date" className="w-full text-xs rounded border px-2 py-1 bg-white mt-0.5"
                          value={trackingData.seguimiento_fecha_examen || ''}
                          onChange={(e) => updateTracking('seguimiento_fecha_examen', e.target.value)}
                          disabled={trackingSaving}
                        />
                      </div>
                      <div>
                        <label className="text-gray-500">{language === 'es' ? 'Profesional' : 'Professional'}</label>
                        <input className="w-full text-xs rounded border px-2 py-1 bg-white mt-0.5"
                          value={trackingData.seguimiento_profesional || ''}
                          onBlur={(e) => e.target.value !== (trackingData.seguimiento_profesional||'') && updateTracking('seguimiento_profesional', e.target.value)}
                          disabled={trackingSaving}
                        />
                      </div>
                      <div>
                        <label className="text-gray-500">{language === 'es' ? 'Institución' : 'Institution'}</label>
                        <input className="w-full text-xs rounded border px-2 py-1 bg-white mt-0.5"
                          value={trackingData.seguimiento_institucion || ''}
                          onBlur={(e) => e.target.value !== (trackingData.seguimiento_institucion||'') && updateTracking('seguimiento_institucion', e.target.value)}
                          disabled={trackingSaving}
                        />
                      </div>
                      <div>
                        <label className="text-gray-500">{language === 'es' ? 'URL constancia' : 'Certificate URL'}</label>
                        <input className="w-full text-xs rounded border px-2 py-1 bg-white mt-0.5"
                          value={trackingData.seguimiento_constancia_url || ''}
                          onBlur={(e) => e.target.value !== (trackingData.seguimiento_constancia_url||'') && updateTracking('seguimiento_constancia_url', e.target.value)}
                          disabled={trackingSaving}
                          placeholder="https://..."
                        />
                      </div>
                    </div>
                    {/* Notas */}
                    <div>
                      <label className="text-xs text-gray-500">{language === 'es' ? 'Notas de seguimiento' : 'Tracking notes'}</label>
                      {trackingData.notas?.length > 0 && (
                        <div className="space-y-1 mt-1 max-h-24 overflow-y-auto">
                          {trackingData.notas.map(n => (
                            <div key={n.id} className="text-xs bg-white p-1.5 rounded border">
                              <span className="text-gray-400">{new Date(n.fecha).toLocaleString()} - {n.autor}:</span> {n.nota}
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-1 mt-1">
                        <input 
                          className="flex-1 text-xs rounded border px-2 py-1 bg-white"
                          value={trackingNota}
                          onChange={(e) => setTrackingNota(e.target.value)}
                          placeholder={language === 'es' ? 'Agregar nota...' : 'Add note...'}
                          onKeyDown={(e) => e.key === 'Enter' && addTrackingNote()}
                          data-testid="tracking-note-input"
                        />
                        <Button size="sm" className="h-7 text-xs px-2" onClick={addTrackingNote} disabled={trackingSaving || !trackingNota.trim()}>
                          +
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t.cerrar}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CanalizacionATSM8L;
